/**
 * AchievementsProvider - Contexto global para achievements
 *
 * Provee el estado de logros y el toast a toda la app.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useAchievements } from '../hooks';
import { AchievementToast } from '../components';
import type { UserAchievementState } from '@/services/achievements';

interface AchievementsContextValue {
  achievementState: UserAchievementState | null;
  recordCompletion: () => void;
  recordPerfectDay: () => void;
  recordHardDayRecovery: () => void;
  recordFeatureUsed: (featureId: string) => void;
  checkAllAchievements: () => void;
  syncStats: () => void;
}

const AchievementsContext = createContext<AchievementsContextValue | null>(null);

interface AchievementsProviderProps {
  children: ReactNode;
}

export function AchievementsProvider({ children }: AchievementsProviderProps) {
  const {
    achievementState,
    newAchievement,
    recordCompletion,
    recordPerfectDay,
    recordHardDayRecovery,
    recordFeatureUsed,
    dismissAchievement,
    checkAllAchievements,
    syncStats,
  } = useAchievements();

  return (
    <AchievementsContext.Provider
      value={{
        achievementState,
        recordCompletion,
        recordPerfectDay,
        recordHardDayRecovery,
        recordFeatureUsed,
        checkAllAchievements,
        syncStats,
      }}
    >
      {children}
      <AchievementToast achievement={newAchievement} onDismiss={dismissAchievement} />
    </AchievementsContext.Provider>
  );
}

export function useAchievementsContext() {
  const context = useContext(AchievementsContext);
  if (!context) {
    throw new Error('useAchievementsContext must be used within AchievementsProvider');
  }
  return context;
}
